import React from 'react';
import { connect } from 'react-redux';
import RailClock from '../common/RailClock';
import { configChange, setSingleView, camViewChange, getNvrTimestamp, setLocalTimestamp, seedCloudTimezone, setCamGroupSingle, setCamGroupQuad } from '../actions';
import { getProductConfig } from '../backend';
import { getProductMode, PRODUCT_MODE } from '../backend/productMode';
import { autoSelectDefaultView, clearView, clearViewDirty, saveView, selectView, setCameraArrangement } from '../actions/ViewActions';
import { clearJumpError, clearAccessNotice } from '../actions/NavigationActions';
import ViewModal from './ViewModal';
import { isCrossServerSlots } from '../util/viewListModel';
import { autoFit, rechunkForDensity, canAddPage as canAddArrangePage, deletePageSlots, pageFillCount, arrangementsEqual, clampWideDensity } from '../util/arrangePages';
import { blankSlots, blankSlot } from '../util/cameraCatalog';
import { ARRANGE_TILE_OPTIONS, ARRANGE_WIDE_TILE_OPTIONS, ARRANGE_WIDE_TIER_MQ, DEFAULT_DENSITY_TABLET, DEFAULT_DENSITY_DESKTOP, confToNumber } from '../util/layouts';
import AlertModal from '../common/AlertModal';
import CamButtonsView from './CamButtonsView';
import JumpControl from './JumpControl';
import ViewList, { getViewDeviceType } from './ViewList';
import DesktopHeader from './DesktopHeader';
import DesktopBottomBar from './DesktopBottomBar';
import LayoutGrid, { confGlyph } from './LayoutGrid';
import { isMobilePortrait } from './dragViewport';
import CameraOverlaysSheet from './CameraOverlaysSheet';
import SystemInfoSheet from './SystemInfoSheet';
import Sheet from '../common/Sheet';
import { formatNvrClock } from '../util/formatClock';
import VideoViewWs from './VideoViewWs';
import ContextMenu from 'react-context-menu';
// WebSocket detection simplified - just check client WebCodecs support
// Server support is validated when VideoViewWs tries to connect
import { setLiveFrame } from '../util/liveFrameCache';
import { setMcpbConf, setMcpbSlots } from '../actions/MCPlaybackActions';
import '../App.css';
import './liveStyle.css';
import { debugLog, debugWarn } from '../util/debug';
import history from '../history';
import MobileBottomNav from '../common/MobileBottomNav';
import MobileTopBar from '../common/MobileTopBar';
import { versionTag } from '../util/releaseVersion';

let camIndex = 0;

// Exported (named) for unit tests; the default export below is the connected component.
export class Live extends React.Component {
    constructor(props) {
        super(props);

        //check if new webcodecs api is available (client-side only)
        this.hasWebCodecs = typeof window !== 'undefined' && typeof window.VideoDecoder !== 'undefined' && typeof window.EncodedVideoChunk !== 'undefined';

        // Check localStorage cache for backend WebSocket support immediately
        // This avoids waiting for async detection on page refresh
        let cachedWsSupport = false;
        let hasCachedValue = false;
        if (this.hasWebCodecs) {
            try {
                const cacheHost = window.location.hostname;
                const cachePort = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
                const cacheKey = `ws_support_${cacheHost}_${cachePort}`;
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const { serverSupport, timestamp } = JSON.parse(cached);
                    const age = Date.now() - timestamp;
                    const cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
                    if (typeof serverSupport === 'boolean' && age < cacheExpiry) {
                        cachedWsSupport = serverSupport;
                        hasCachedValue = true;
                        debugLog(`[LiveView] Using cached WebSocket support: ${cachedWsSupport} (age: ${Math.round(age / 1000)}s)`);
                    }
                }
            } catch (e) {
                // Ignore cache errors
            }
        }

        this.state = {
            autoScanning: false,
            timestampHandlerRunning: false,
            wsDetectionComplete: hasCachedValue, // If we have a valid cached value, mark detection as complete
            wsFailedFallback: false, // True if we fell back to JPEG due to WS failures
            useWebSockets: cachedWsSupport, // Use cached value immediately (true or false)
            mobileMenuOpen: null, // null, 'grid', 'views', 'jump', 'overlays'
            fsViewsOpen: false,
            fsGridOpen: false,
            sysInfoOpen: false,   // mobile/tablet System-details sheet (#86bb5dx62)
            // Desktop redesign (#86bb5dx62, Mock D4): the left-rail slide-out panel + the header
            // breadcrumb share THIS key (separate from mobileMenuOpen so the desktop panels and
            // the mobile bottom-sheets never both mount their reused content). 'jump'|'views'|'grid'.
            desktopPanel: null,
            // Desktop video-wall idle (#86bb5dx62, David): after ~10s with no mouse movement, hide
            // the cursor + the tile hover affordances (accent border / drag handle / ⋯) so a resting
            // wall is clean. Wakes on the next mousemove. Fine-pointer (mouse) only.
            mouseIdle: false,
            navGuard: { open: false, pending: null }, // unsaved-view prompt before leaving Live
            // tablet Arrange mode (#86bb5dx62): full-screen view editor. editMode on/off + its
            // working state (density = tiles per page, page = which chunk, name, mode, and snapshots
            // for dirty/reset/redo). arrangeOriginal/Redo hold camera-id-by-slot snapshots.
            editMode: false,
            arrangeDensity: 8,     // tiles per page (4/8/16)
            arrangePage: 0,        // current page (window into the 64-slot cameras array)
            arrangePageCount: 1,   // pages the user currently sees (>= the content span)
            arrangeName: '',       // the top-bar view name (drives Save-enable + prefills the modal)
            arrangeMode: 'edit',   // 'edit' (current view) | 'new' (blank canvas)
            arrangeConfirmCancel: false,            // Cancel-with-changes confirm sheet
            arrangeDeleteConfirm: null,             // { page, count } delete-page-with-cameras confirm
            arrangeSaveOpen: false,                 // the unified Save modal
            arrangeSaveError: null,                 // save failure message (keeps arrange open to retry)
            arrangeOriginal: null,                  // dirty/reset baseline (edit = loaded view · new = blank)
            arrangePreEntry: null,                  // the live cameras BEFORE arrange — restored on cancel/discard
            arrangeRedo: null,                      // pre-reset snapshot for single-level "Undo reset"
            treeCollapsed: false                    // slide-rail: tree collapsed to a thin "Cameras" rail
        };
        this.autoScanTimerHandler = 0;
        this.nvrTimestampHandler = 0;
        this.wsFailureCount = 0; // Track consecutive websocket failures
        this.wsRetryTimer = null; // Timer to retry websockets after fallback
    };

    componentDidMount = async () => {
      clearInterval(this.autoScanTimerHandler);
      clearInterval(this.nvrTimestampHandler);
      camIndex = 0;
      this.setState({
        autoScanning: false
      })
      // Header clock: local NVR polls getNvrTimestamp (NVR time via RPC offset + local tick, resync
      // ~10 min). Cloud has no NVR to poll, so it ticks locally — but now in the SITE zone
      // (JSCustomerProject.sTimezone) rather than plain browser-local: seedCloudTimezone reads the
      // site zone from the cloud session into state.utility.nvrTimezone HERE so it works on both a
      // fresh login and a cached-session RESUME (#86bbm5p92). Neither clock is driven by camera
      // metadata — that's per-tile only, and mixing it into the header made it oscillate. #86bb5dx62
      if(!this.state.timestampHandlerRunning){
        if (getProductConfig().pollsNvrClock) {
          this.startNvrTimeUpdate()
        } else {
          this.props.seedCloudTimezone();
          this.startLocalTimeUpdate()
        }
      }
      this.props.setCamGroupSingle(1);
      this.props.setCamGroupQuad(1);

      // Initial detection - check if WebSocket is available
      // Only run if session AND cameras are available
      const hasSession = !!this.props.sSess;
      const hasCameras = this.props.cameras && this.props.cameras.length > 0;

      if (hasSession && hasCameras) {
        await this.detectWebSocketSupport(false);
      } else {
        debugLog(`[LiveView] Waiting for data: session=${hasSession}, cameras=${hasCameras ? this.props.cameras.length : 0}`);
        // Don't mark detection as complete - componentDidUpdate will trigger detection
        // when session and cameras become available
      }

      // Auto-select default view if data and views are already loaded
      if (this.props.dataLoaded && this.props.views?.length > 0 && !this.props.currentView) {
        debugLog('[LiveView] Data already loaded on mount, auto-selecting default view');
        this.props.autoSelectDefaultView();
      }

      // Restore the clean saved view if a refresh landed mid-Arrange / on an abandoned edit.
      this.healViewOnLoad();

      // Option A (#86bb7phhh): if we arrived here from Playback's Views sheet asking to Arrange
      // (Arrange current view / New view / a per-view ✎), enter that Arrange mode now.
      this.consumeArrangeRequest();

      // Listen for Escape to exit fullscreen (handler is a class method so it stays unit-testable).
      document.addEventListener('keydown', this.handleEscapeKey);

      // Arrange wide-density guard (#86bb7phhh): the 32/All steps exist only in the landscape+desktop
      // tier (ARRANGE_WIDE_TIER_MQ, mirrored by the `.live-arrange-seg__btn--wide` @media). If the
      // viewport leaves that tier while a wide density is active — e.g. a tablet rotates
      // landscape→portrait — drop back to 16 so portrait never sits on a grid whose (now-hidden)
      // control can't change it.
      if (typeof window !== 'undefined' && window.matchMedia) {
        this._wideTierMql = window.matchMedia(ARRANGE_WIDE_TIER_MQ);
        this._onWideTierChange = () => this._clampArrangeDensityToTier();
        if (this._wideTierMql.addEventListener) this._wideTierMql.addEventListener('change', this._onWideTierChange);
        else if (this._wideTierMql.addListener) this._wideTierMql.addListener(this._onWideTierChange); // legacy Safari
      }

      // Desktop video-wall idle-hide (#86bb5dx62, David): fine-pointer (mouse) only. After ~10s
      // of no movement, hide the cursor + the tile hover affordances; wake on the next mousemove.
      this._finePointer = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: fine)').matches;
      if (this._finePointer) {
        this._onMouseMove = () => {
          if (this.state.mouseIdle) this.setState({ mouseIdle: false });
          clearTimeout(this._idleTimer);
          this._idleTimer = setTimeout(() => this.setState({ mouseIdle: true }), 10000);
        };
        document.addEventListener('mousemove', this._onMouseMove);
        this._idleTimer = setTimeout(() => this.setState({ mouseIdle: true }), 10000);
      }
    }


    componentDidUpdate = async (prevProps) => {
      // Fullscreen pickers belong to this fullscreen session, including exits from external controls.
      if (prevProps.fFullscreen && !this.props.fFullscreen) {
        this.setState({ fsViewsOpen: false, fsGridOpen: false });
      }
      // A Jump System / re-login swaps the active NVR — give the new server a clean
      // WS attempt (see resetWsFallbackOnSessionChange) BEFORE any view/stream logic.
      this.resetWsFallbackOnSessionChange(prevProps);

      // Check if session AND cameras just became available
      const hadNoSession = !prevProps.sSess;
      const hadNoCameras = !prevProps.cameras || prevProps.cameras.length === 0;
      const hasSessionNow = !!this.props.sSess;
      const hasCamerasNow = this.props.cameras && this.props.cameras.length > 0;

      // Run detection when we have both session and cameras, and haven't detected yet
      const wasNotReady = hadNoSession || hadNoCameras;
      const isReadyNow = hasSessionNow && hasCamerasNow;

      if (wasNotReady && isReadyNow && !this.state.wsDetectionComplete) {
        debugLog(`[LiveView] Data ready (session + ${this.props.cameras.length} cameras), running WebSocket detection`);
        await this.detectWebSocketSupport(false);
      }

      // Auto-select default view when data or views become available
      const dataJustLoaded = !prevProps.dataLoaded && this.props.dataLoaded;
      const viewsJustLoaded = (prevProps.views?.length || 0) === 0 && (this.props.views?.length || 0) > 0;
      const serialJustLoaded = (!prevProps.bSerial || prevProps.bSerial === 0) && this.props.bSerial && this.props.bSerial !== 0;
      const noViewSelected = !this.props.currentView;
      const hasViews = this.props.views?.length > 0;

      // Trigger auto-select when:
      // 1. Data just loaded, OR
      // 2. Views just loaded, OR
      // 3. Serial just became available (needed for localStorage lookup)
      const shouldAutoSelect = (dataJustLoaded || viewsJustLoaded || serialJustLoaded) && hasViews && noViewSelected;

      if (shouldAutoSelect) {
        debugLog('[LiveView] Auto-selecting default view (dataJustLoaded:', dataJustLoaded, 'viewsJustLoaded:', viewsJustLoaded, 'serialJustLoaded:', serialJustLoaded, ')');
        this.props.autoSelectDefaultView();
      }

      // Restore the clean saved view once the slots are available (one-shot; see componentDidMount).
      this.healViewOnLoad();

      // Auto-select default view when active server changes (eview mode / jump system)
      // Also stop auto-scan when jumping to a different server
      if (this.props.fEview && prevProps.activeServerSerial && this.props.activeServerSerial !== prevProps.activeServerSerial) {
        debugLog('[LiveView] Active server changed from', prevProps.activeServerSerial, 'to', this.props.activeServerSerial);
        // Stop auto-scan when jumping between systems (TODO-7)
        this.killAutoScan();
        this.props.clearView();
        setTimeout(() => {
          this.props.autoSelectDefaultView();
        }, 50);
      }
    }

    detectWebSocketSupport = async (forceCheck = false) => {
      // Just check client-side WebCodecs support
      // Skip server test - VideoViewWs will try WS directly and fall back to JPEG if needed
      debugLog('[LiveView] Checking WebSocket capabilities...');

      if (this.hasWebCodecs) {
        debugLog('[LiveView] Client supports WebCodecs, enabling WebSocket streaming');
        this.setState({
          useWebSockets: true,
          wsDetectionComplete: true
        });
      } else {
        debugLog('[LiveView] Client does not support WebCodecs, using JPEG polling');
        this.setState({
          useWebSockets: false,
          wsDetectionComplete: true
        });
      }
    }

    componentWillUnmount = () => {
      clearInterval(this.autoScanTimerHandler);
      clearInterval(this.nvrTimestampHandler);
      clearTimeout(this.wsRetryTimer);
      clearTimeout(this._idleTimer);
      document.removeEventListener('keydown', this.handleEscapeKey);
      if (this._onMouseMove) document.removeEventListener('mousemove', this._onMouseMove);
      if (this._wideTierMql && this._onWideTierChange) {
        if (this._wideTierMql.removeEventListener) this._wideTierMql.removeEventListener('change', this._onWideTierChange);
        else if (this._wideTierMql.removeListener) this._wideTierMql.removeListener(this._onWideTierChange);
      }
      camIndex = 0
      this.setState({
        autoScanning: false,
        timestampHandlerRunning: false,
        loaded: false
      });
    }

    // Reset the WS JPEG-fallback breaker when the active session changes (a Jump
    // System / re-login → a DIFFERENT NVR). The breaker is per-connection: after 3
    // WS failures it drops to JPEG for 5 minutes (handleWebSocketFailure). Without
    // this reset, a previous NVR whose backend doesn't serve /ws/ (404 handshake)
    // trips that lockout and a jump to a fully WS-capable NVR inherits it — stuck on
    // JPEG with no WS badge / metadata / object overlays until the timer expires.
    // Clearing wsFailureCount + wsFailedFallback and re-enabling useWebSockets gives
    // the new server its own clean 3-attempt WS try (VideoViewWs re-queues the
    // upgrades because wsBackendSupported + sSess are in its effect deps).
    resetWsFallbackOnSessionChange = (prevProps) => {
      const jumped = prevProps.sSess && this.props.sSess && prevProps.sSess !== this.props.sSess;
      if (!jumped) return;
      debugLog('[LiveView] Session changed (jump/re-login) — resetting WS fallback for the new server');
      clearTimeout(this.wsRetryTimer);
      this.wsFailureCount = 0;
      if (this.state.wsFailedFallback || this.state.useWebSockets !== this.hasWebCodecs) {
        this.setState({ wsFailedFallback: false, useWebSockets: this.hasWebCodecs });
      }
    }

    // Called by CameraStreamContainerWs when websocket connection fails
    handleWebSocketFailure = () => {
      this.wsFailureCount++;
      debugLog(`[LiveView] WebSocket failure #${this.wsFailureCount}`);

      // After 3 consecutive failures, fallback to JPEG polling
      if (this.wsFailureCount >= 3 && !this.state.wsFailedFallback) {
        debugWarn('[LiveView] Multiple websocket failures detected, falling back to JPEG polling');
        this.setState({
          wsFailedFallback: true,
          useWebSockets: false
        });

        // Retry websockets after 5 minutes
        this.wsRetryTimer = setTimeout(() => {
          debugLog('[LiveView] Retrying websocket streaming after fallback...');
          this.wsFailureCount = 0;
          this.setState({
            wsFailedFallback: false,
            useWebSockets: true
          });
        }, 5 * 60 * 1000); // 5 minutes
      }
    }

    // Called by CameraStreamContainerWs when websocket successfully connects
    handleWebSocketSuccess = () => {
      // Reset failure count on successful connection
      if (this.wsFailureCount > 0) {
        debugLog('[LiveView] WebSocket connection successful, resetting failure count');
        this.wsFailureCount = 0;
      }

      // If we had fallen back, log that we've recovered
      if (this.state.wsFailedFallback) {
        debugLog('[LiveView] WebSocket streaming restored after fallback');
        this.setState({ wsFailedFallback: false });
      }
    }

    startNvrTimeUpdate = () => {
      // Always start browser-based timer as fallback
      // Camera metadata will override when available (websocket mode)
      // Call immediately first to set initial timestamp (prevents camera load delay)
      this.props.getNvrTimestamp(this.props.sSess);
      this.nvrTimestampHandler = setInterval(() => this.props.getNvrTimestamp(this.props.sSess), 1000);
    }

    startLocalTimeUpdate = () => {
      // Cloud: the header clock is plain browser-local time (no NVR poll, no TZ sent).
      this.props.setLocalTimestamp();
      this.nvrTimestampHandler = setInterval(() => this.props.setLocalTimestamp(), 1000);
    }

    exitFullscreenHamdler= () => {
      this.props.configChange('conf-fs');
    }

    // Escape in fullscreen exits it — but ONE LEVEL AT A TIME: if a fullscreen picker sheet is open, the
    // Sheet's own Escape closes the picker first and this guard skips the exit; a second Escape then exits
    // fullscreen. Standard layered-Escape (David 2026-09-10). Desktop-only — phones have no Escape key.
    handleEscapeKey = (e) => {
      if (e.key === 'Escape' && this.props.fFullscreen && !this.state.fsViewsOpen && !this.state.fsGridOpen) {
        this.props.configChange('conf-fs');
      }
    }

    // Fullscreen picker toggles — mutually exclusive (only one sheet open at a time) and pressing a
    // control again closes its own sheet. (David 2026-09-10.)
    toggleFsViews = () => this.setState((s) => ({ fsViewsOpen: !s.fsViewsOpen, fsGridOpen: false }));
    toggleFsGrid = () => this.setState((s) => ({ fsGridOpen: !s.fsGridOpen, fsViewsOpen: false }));

    // for single cam scan we are just going to increment the current cam view

    startAutoScan = () => {
      // get index of current cam selected in our enabled Cams array
      switch (this.props.conf) {
        case "conf-1":
          let cam1 = this.props.singleViewEnabledArr.indexOf(parseInt(this.props.currentCamView.slice(4, this.props.userCameras.length)));
          if(cam1 > -1){
            camIndex = cam1;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-2":
          let cam2 = (this.props.twoViewEnabledArr || []).indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam2 > -1){
            camIndex = cam2;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-4":
          let cam4 = this.props.quadViewEnabledArr.indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam4 > -1){
            camIndex = cam4;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-6":
          let cam6 = this.props.sixViewEnabledArr.indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam6 > -1){
            camIndex = cam6;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-8":
          let cam8 = (this.props.eightViewEnabledArr || []).indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam8 > -1){
            camIndex = cam8;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-9":
          let cam9 = this.props.nineViewEnabledArr.indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam9 > -1){
            camIndex = cam9;
          } else {
            camIndex = 0;
          }
          break;
        case "conf-16":
          let cam16 = this.props.sixteenViewEnabledArr.indexOf(this.props.currentCamView.slice(4, this.props.currentCamView.length));
          if(cam16 > -1){
            camIndex = cam16;
          } else {
            camIndex = 0;
          }
          break;
        default:
          camIndex = 0;
      }

      this.setState({
        autoScanning: true
      });
      // clear single cam view if we have a cam that was enlarged using double click
      this.props.singleCamSelected && this.props.setSingleView('')
      // set the interval timer to call scanStart function
      this.autoScanTimerHandler = setInterval(this.scan, 8000);  // bAutoScanTimeout
    }

    scan = () => {

      // Never swap tiles while the user is arranging/editing a view: the 8s scan tick would rewrite the
      // grid being edited out from under them (and spuriously flip the unsaved-dirty dot). Skip the tick;
      // scanning resumes when edit mode exits. Guards EVERY edit-mode entry path (branch-review, HIGH).
      if (this.state.editMode) return;

      // initial scan cam is blank when moving to another this.props.conf - cam_ undefined
      // if our local as variable gets set to false clear the interval timer
      if(!this.state.autoScanning) {
        this.killAutoScan();
      }

      // single cam view
      if(this.props.conf === 'conf-1' && this.props.singleViewEnabledArr.length > 1) {
        // rotate through changing our cameras
        this.props.camViewChange(`cam_` + this.props.singleViewEnabledArr[camIndex]);

        // if the cam number is less than the number of cameras that are enabled, increment the curent cam set by 1
        // otherwise set the current cam back to 1
        if(camIndex < this.props.singleViewEnabledArr.length - 1){
          camIndex = camIndex + 1
        } else {
          camIndex = 0
        }

        // if we have over 16 cams set the correct camGroup to display the correct set of buttons
        if( camIndex <= 15 ) {
          this.props.setCamGroupSingle(1)
        }
        if( camIndex > 15 &&  camIndex <= 31 ) {
          this.props.setCamGroupSingle(2)
        }
        if( camIndex > 31 &&  camIndex <= 47 ) {
          this.props.setCamGroupSingle(3)
        }
        if( camIndex > 47 &&  camIndex <= 63 ) {
          this.props.setCamGroupSingle(4)
        }

      }

        // two cam view
        if(this.props.conf === 'conf-2' && (this.props.twoViewEnabledArr || []).length > 1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.twoViewEnabledArr[camIndex]);
            if(camIndex < this.props.twoViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }
        }

        // quad cam view
        if(this.props.conf === 'conf-4' && this.props.quadViewEnabledArr.length > 1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.quadViewEnabledArr[camIndex]);
            // if the cam number isless than the number of cameras we support, increment the curent cam set by 1
            // otherwise set the current cam back to index 0
            if(camIndex < this.props.quadViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }

            // if we have over 32 cams set the correct camGroup to display the correct set of buttons
            if( camIndex <= 31 ) {
              this.props.setCamGroupSingle(1)
            }
            if( camIndex > 31 ) {
              this.props.setCamGroupQuad(2)
            }
        }

        // octo cam view
        if(this.props.conf === 'conf-6' && this.props.sixViewEnabledArr.length > 1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.sixViewEnabledArr[camIndex]);
            // if the cam number isless than the number of cameras we support, increment the curent cam set by 1
            // otherwise set the current cam back to index 0
            if(camIndex < this.props.sixViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }
        }

        // eight cam view (asymmetric 1-big+7)
        if(this.props.conf === 'conf-8' && (this.props.eightViewEnabledArr || []).length > 1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.eightViewEnabledArr[camIndex]);
            if(camIndex < this.props.eightViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }
        }

        // nine cam view
        if(this.props.conf === 'conf-9' && this.props.nineViewEnabledArr.length > 1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.nineViewEnabledArr[camIndex]);
            // if the cam number isless than the number of cameras we support, increment the curent cam set by 1
            // otherwise set the current cam back to index 0
            if(camIndex < this.props.nineViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }
        }

        // twelve cam view
        if(this.props.conf === 'conf-12' && this.props.twelveViewEnabledArr.length > 1){
          // rotate through changing our cameras
          this.props.camViewChange(`cam_` + this.props.twelveViewEnabledArr[camIndex]);
          // if the cam number isless than the number of cameras we support, increment the curent cam set by 1
          // otherwise set the current cam back to index 0
          if(camIndex < this.props.twelveViewEnabledArr.length - 1){
            camIndex++
          } else {
            camIndex = 0
          }
        }

        // sixteen cam view
        if(this.props.conf === 'conf-16' && this.props.sixteenViewEnabledArr.length >  1){
            // rotate through changing our cameras
            this.props.camViewChange(`cam_` + this.props.sixteenViewEnabledArr[camIndex]);
            // if the cam number isless than the number of cameras we support, increment the curent cam set by 1
            // otherwise set the current cam back to index 0
            if(camIndex < this.props.sixteenViewEnabledArr.length - 1){
              camIndex++
            } else {
              camIndex = 0
            }
        }
    }

    killAutoScan = () => {
      camIndex = 0;
      clearInterval(this.autoScanTimerHandler);
      this.setState({ autoScanning: false })
      // make sure we do not have a camera set that has been double clicked for enlargement - shouldnt be necessary, but...
      this.props.singleCamSelected && this.props.setSingleView('');
    }

    toggleAutoScan = () => {
      if(this.state.autoScanning) {
        this.killAutoScan();
      } else {
        this.startAutoScan();
      }
    }

    // Mobile bar: cycle grid layout (1 → 4 → 9 → 1)
    cycleGridLayout = () => {
      this.killAutoScan();
      this.props.singleCamSelected && this.props.setSingleView('');
      const cycle = { 'conf-1': 'conf-4', 'conf-4': 'conf-9', 'conf-9': 'conf-1' };
      const next = cycle[this.props.conf] || 'conf-4';
      this.props.configChange(next);
      const camMap = { 'conf-1': 'cam_1', 'conf-4': 'cam_1-4', 'conf-9': 'cam_1-9' };
      this.props.camViewChange(camMap[next] || 'cam_1-4');
      if (next === 'conf-1') this.props.setCamGroupSingle(1);
      if (next === 'conf-4') this.props.setCamGroupQuad(1);
    }

    // Mobile grid picker: select a specific grid layout
    selectGridLayout = (conf) => {
      this.killAutoScan();
      this.props.singleCamSelected && this.props.setSingleView('');
      this.props.configChange(conf);
      const camMap = {
        'conf-1': 'cam_1', 'conf-2': 'cam_1-2', 'conf-4': 'cam_1-4', 'conf-6': 'cam_1-6',
        'conf-8': 'cam_1-8', 'conf-9': 'cam_1-9', 'conf-12': 'cam_1-12', 'conf-16': 'cam_1-16'
      };
      this.props.camViewChange(camMap[conf] || 'cam_1-4');
      if (conf === 'conf-1') this.props.setCamGroupSingle(1);
      if (conf === 'conf-4') this.props.setCamGroupQuad(1);
      this.closeMobileMenu();
      this.closeDesktopPanel();   // desktop: return to full-bleed video after picking a layout (matches Jump/Views)
    }

    // The Views button doubles as the current-view indicator (David #86bb5dx62): its label is
    // the loaded view's name (fallback "Views" when none / a synthetic set is active). A dirty
    // dot flags unsaved live-grid changes. Desktop's sidebar dropdown already shows the selection.
    viewLabel = () => {
      const v = this.props.currentView;
      return (v && !String(v).startsWith('__synthetic_')) ? v : 'Views';
    };

    // Genuine unsaved changes vs the pristine saved layout (David #86bb5dx62): compares the
    // working cameras to the snapshot taken when the view was selected/saved, so reverting a
    // live drag-swap back to the original CLEARS the dot (unlike the coarse Redux isDirty flag).
    liveViewDirty = () => {
      const { currentView, cameras, pristineCameras, isDirty, isJumping } = this.props;
      if (isJumping) return false;  // mid server-switch: state settles across systems, never flash "unsaved"
      if (!currentView || String(currentView).startsWith('__synthetic_')) return false;
      if (!isDirty) return false;  // no real user edit → never dirty (a jump/view-load resets isDirty, so it can't false-light)
      if (!pristineCameras || !pristineCameras.length) return false;
      return !arrangementsEqual(cameras || [], pristineCameras);
    };

    toggleMobileMenu = (panel) => {
      this.setState(prev => ({
        mobileMenuOpen: prev.mobileMenuOpen === panel ? null : panel
      }));
    }

    closeMobileMenu = () => {
      this.setState({ mobileMenuOpen: null });
    }

    // Desktop left-rail panel (Jump/Views/Layout) + header breadcrumb (#86bb5dx62). Toggle =
    // click the open tab/crumb again to close (full-bleed). Only one panel open at a time.
    toggleDesktopPanel = (panel) => {
      this.setState(prev => ({ desktopPanel: prev.desktopPanel === panel ? null : panel }));
    }
    closeDesktopPanel = () => this.setState({ desktopPanel: null });

    // The desktop slide-out panel body, by active tab. Reuses the SAME components as the mobile
    // sheets (JumpControl/ServerView, ViewList, renderLayoutGrid) — the desktop panel is just a
    // different shell around them (#86bb5dx62, Mock D4).
    renderDesktopPanel = () => {
      const open = this.state.desktopPanel;
      if (open === 'jump') {
        return (
          <div className="live-dpane">
            <div className="live-dpane__hd"><b>Jump to system</b><button className="live-dpane__x" onClick={this.closeDesktopPanel} aria-label="Close">&times;</button></div>
            <div className="live-dpane__body">
              {/* Persist (#86bb7phhh, David): the slide-out stays open after a jump — only the × or
                  re-clicking the crumb closes it. So NO onJumpStarted close here (the mobile sheet,
                  which does close on select, keeps its own onClose in the sheet render below). */}
              <JumpControl variant="list" />
            </div>
          </div>
        );
      }
      if (open === 'views') {
        return (
          <div className="live-dpane">
            <div className="live-dpane__hd"><b>Views</b><button className="live-dpane__x" onClick={this.closeDesktopPanel} aria-label="Close">&times;</button></div>
            <div className="live-dpane__body">
              {/* View management — Arrange the current view, or start a blank New view. */}
              <div className="live-arrange-entries">
                <button className="live-arrange-entry" onClick={() => { this.closeDesktopPanel(); this.enterEditMode(); }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  <span>Arrange view</span>
                </button>
                <button className="live-arrange-entry live-arrange-entry--new" onClick={() => { this.closeDesktopPanel(); this.enterNewView(); }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
                  <span>New view</span>
                </button>
              </div>
              {/* Persist (#86bb7phhh, David): selecting a view keeps the panel open (no onClose) — only
                  the × / crumb re-click closes it. onArrange still closes because it navigates into Arrange. */}
              <ViewList onArrange={(v) => { this.closeDesktopPanel(); this.arrangeSpecificView(v); }} />
            </div>
          </div>
        );
      }
      // Layout is no longer a desktop panel — it moved to the bottom-bar LayoutMenu (#86bb5dx62).
      return null;
    };

    openSetup = () => {
      // Setup is the ONE shared shell for EVERY product — SetupView at /setup (David 2026-08-05). Local
      // shows the Display module only; cloud shows the full config set. No more per-product overlay fork:
      // always route to the shell (the old standalone Display/Theme SetupOverlay is retired).
      history.push('/setup');
    }

    // System-details sheet (#86bb5dx62) — the mobile/tablet home for the desktop bottom-bar ⓘ,
    // opened by the ⓘ in the mobile top bar (portrait) or the landscape rail.
    openSysInfo = () => this.setState({ sysInfoOpen: true, mobileMenuOpen: null });
    closeSysInfo = () => this.setState({ sysInfoOpen: false });

    // Desktop redesign (#86bb5dx62, Mock D4): the header Live/Playback toggle + the account
    // menu route to Playback. Legacy single-cam playback is retired — Playback = MCPlayback only.
    // Guarded so an unsaved live arrangement prompts before leaving.
    goToPlayback = () => {
      const { currentView, viewSlots, allCameras } = this.props;
      const allCamsArr = allCameras ? Object.values(allCameras) : [];
      const devType = getViewDeviceType(currentView, viewSlots, allCamsArr);
      if (devType === 'legacy') {
        this.setState({ legacyPlaybackAlert: true });
        return;
      }
      // Sync the live view's current camera layout into playback so switching
      // from e.g. 9-cam live shows 9 cams in playback, not the stale persisted 4.
      const liveConf = confToNumber(this.props.conf);
      const liveSlotArr = (viewSlots && viewSlots[this.props.sName]) || [];
      const camsByKey = {};
      (allCamsArr).forEach((c) => { if (c) camsByKey[c.bSerial + '_' + c.bCamera] = c; });
      const pbSlots = Array.from({ length: liveConf }, (_, i) => {
        const ls = liveSlotArr[i];
        if (!ls) return null;
        const full = camsByKey[ls.bSerial + '_' + ls.bCamera];
        return full || { bCamera: ls.bCamera, bSerial: ls.bSerial, sName: '', fEnable: true, sDeviceID: '', source: '' };
      });
      this.props.setMcpbConf(liveConf);
      this.props.setMcpbSlots(pbSlots);

      // Capture each visible live canvas as a preview for playback cells.
      // Store under BOTH camera.id (live's key) AND camera.bID (playback's key)
      // so the lookup never misses due to a field-name mismatch.
      // WebCodecs-painted canvases may be tainted → copy to a temp canvas first.
      const camMap = allCameras || {};
      document.querySelectorAll('canvas[data-cam-id]').forEach((canvas) => {
        try {
          const camId = canvas.getAttribute('data-cam-id');
          if (!camId || canvas.width <= 0 || canvas.height <= 0) return;
          let dataUrl;
          try {
            dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          } catch (_taint) {
            const tmp = document.createElement('canvas');
            tmp.width = canvas.width;
            tmp.height = canvas.height;
            const ctx = tmp.getContext('2d');
            if (ctx) {
              ctx.drawImage(canvas, 0, 0);
              dataUrl = tmp.toDataURL('image/jpeg', 0.85);
            }
          }
          if (dataUrl && dataUrl.length > 100) {
            setLiveFrame(camId, dataUrl);
            // Also store under bID if it differs from id (playback looks up by bID).
            const cam = camMap[camId];
            if (cam && cam.bID && cam.bID !== camId) setLiveFrame(cam.bID, dataUrl);
          }
        } catch (_) { /* per-canvas best-effort */ }
      });
      this.guardedNav(() => history.push('/mcplayback'));
    };

    // Overlays-gear actions (bottom-left). Broadcast to the tiles: each stream/zoom hook
    // listens and acts on itself (it owns its own canvas + zoom state). #86bb5dx62.
    onSnapshotAllTiles = () => { try { window.dispatchEvent(new CustomEvent('dview-snapshot-all-tiles')); } catch (e) { /* older browser */ } };
    onResetAllZoom = () => { try { window.dispatchEvent(new CustomEvent('dview-reset-all-zoom')); } catch (e) { /* older browser */ } };

    // The Layout picker grid — now the shared LayoutGrid component (#86bb5dx62). Still used by the
    // mobile Layout bottom sheet here; the desktop bottom-bar LayoutMenu renders the same grid.
    renderLayoutGrid = () => (
      <LayoutGrid conf={this.props.conf} onSelect={this.selectGridLayout} />
    );

    // Navigate-away guard (#86bb5dx62 L-A4/L-C4): a drag-swap / assign / clear marks the view
    // dirty (SET_VIEW_DIRTY). If the user tries to leave Live with unsaved arrangement changes,
    // prompt to save or discard first. `proceed` is the real navigation fn from MobileBottomNav
    // or the landscape bar; it only runs once the user resolves the prompt.
    guardedNav = (proceed) => {
      if (this.props.isDirty) {
        this.setState({ navGuard: { open: true, pending: proceed } });
      } else if (typeof proceed === 'function') {
        proceed();
      }
    }

    navGuardDiscard = () => {
      const proceed = this.state.navGuard.pending;
      this.props.clearViewDirty();
      this.setState({ navGuard: { open: false, pending: null } });
      if (typeof proceed === 'function') proceed();
    }

    navGuardCancel = () => {
      this.setState({ navGuard: { open: false, pending: null } });
    }

    // Open the Views sheet so the user can complete the save. The ViewList shows a contextual
    // "Save changes to <view>" banner whenever the current view has unsaved edits, so this is
    // where the nav-guard "Save view…" lands. (Replaces the old <select> DOM-poke.)
    triggerUnifiedSave = () => {
      this.setState({ mobileMenuOpen: 'views' });
    }

    // Arrange a SPECIFIC view in one click (ViewList ✎): select it, then enter Arrange once
    // its layout has landed. If it's already the current view, arrange straight away.
    arrangeSpecificView = (viewName) => {
      this.closeMobileMenu();
      if (viewName === this.props.currentView) { this.enterEditMode(); return; }
      this.props.selectView(viewName);
      setTimeout(() => this.enterEditMode(), 60);
    }

    navGuardSave = () => {
      // We don't auto-run the pending navigation; the user saves, then navigates.
      this.setState({ navGuard: { open: false, pending: null } });
      this.triggerUnifiedSave();
    }

    // Tablet Arrange mode (#86bb5dx62): edit the CURRENTLY-LOADED view in place — a camera-only
    // tree (drag onto a tile to assign) + per-tile ✕ clear + tile↔tile swap. Commit with the
    // unified Save (Update this view / Save as new); "Done" just exits, keeping the working
    // arrangement (the nav-guard still catches a dirty leave). One view context throughout —
    // there is no separate edit-view selector (redesign decision, David 2026-07-31).
    // Restore the clean saved view ON LOAD (#86bb5dx62). A screen refresh mid-Arrange (or an
    // abandoned New view / edit) rehydrates the mutated working grid from redux-persist while
    // the view NAME stays selected → the header shows the view with a blank/half-edited grid.
    // Re-select the view to rebuild it from the (intact) saved slots. Never a backend change
    // (only saveView/deleteView write). Runs ONCE, in the initial-load window: once the slots
    // are available it evaluates and latches `_healedOnLoad`, so it can never undo a genuine
    // in-session live edit later. Unsaved edits not surviving a refresh is the expected behavior.
    healViewOnLoad = () => {
      if (this._healedOnLoad) return;
      const { currentView, cameras, viewSlots, isDirty } = this.props;
      if (!currentView || currentView.startsWith('__synthetic_')) return; // wait for a real view
      const vs = viewSlots || {};
      const hasSlots = (vs[currentView] && vs[currentView].length > 0)
        || Object.keys(vs).some(k => k.startsWith(currentView + '_') && vs[k] && vs[k].length > 0);
      if (!hasSlots) return; // slots not loaded yet — retry on a later update
      this._healedOnLoad = true; // evaluated once → never fights an in-session edit hereafter
      const enabled = (cameras || []).filter(c => c && c.fEnable).length;
      if (enabled === 0 || isDirty) {
        debugLog('[LiveView] Restoring clean view on load (abandoned unsaved edit):', currentView);
        this.props.selectView(currentView);
      }
    };

    // Serial to tag empty placeholder slots with (cosmetic — save ignores disabled slots).
    arrangeSerial = () => (this.props.fEview ? (this.props.activeServerSerial || 0) : (this.props.bSerial || 0));

    // Entry default density: denser on bigger screens (David — tablet 8, desktop 16). A
    // one-shot read (not a reactive style), so it doesn't break the fluid CSS system.
    // Threshold is the app desktop boundary (1280, conformance 2026-09-01) so 1024–1279 tablet-landscape
    // gets the tablet default, matching its chrome.
    defaultDensity = () => (typeof window !== 'undefined' && window.innerWidth >= 1280 ? DEFAULT_DENSITY_DESKTOP : DEFAULT_DENSITY_TABLET);

    // Shallow-clone the working array (compared by identity fields, restored by dispatch).
    snapshotCameras = () => (this.props.cameras || []).map(c => ({ ...c }));

    // Arrange = edit the CURRENTLY-LOADED view in place. Auto-opens at the smallest
    // layout that fits (19-cam view → 16 grid, 2 pages). One view context throughout —
    // no separate edit-view selector (redesign decision, David 2026-07-31).
    enterEditMode = () => {
      const snap = this.snapshotCameras();
      const { density, pageCount } = autoFit(snap, this.defaultDensity(), ARRANGE_TILE_OPTIONS);
      this.setState({
        editMode: true, mobileMenuOpen: null, arrangeMode: 'edit',
        arrangeDensity: density, arrangePage: 0, arrangePageCount: pageCount,
        arrangeName: (this.props.currentView && !String(this.props.currentView).startsWith('__synthetic_')) ? this.props.currentView : '',
        arrangeOriginal: snap, arrangePreEntry: snap, arrangeRedo: null,
        arrangeConfirmCancel: false, arrangeDeleteConfirm: null, arrangeSaveOpen: false, arrangeSaveError: null, treeCollapsed: false
      });
    };

    // ＋New view = a blank canvas (a CREATE action, distinct from Save). Wipe to empty
    // slots + empty name ("Name this view"); keep the pre-entry live view to restore on cancel.
    enterNewView = () => {
      const preEntry = this.snapshotCameras();
      const blank = blankSlots(64, this.arrangeSerial());
      this.props.setCameraArrangement(blank, false); // clean slate (not dirty yet)
      this.setState({
        editMode: true, mobileMenuOpen: null, arrangeMode: 'new',
        arrangeDensity: this.defaultDensity(), arrangePage: 0, arrangePageCount: 1,
        arrangeName: '', arrangeOriginal: blank.map(c => ({ ...c })), arrangePreEntry: preEntry, arrangeRedo: null,
        arrangeConfirmCancel: false, arrangeDeleteConfirm: null, arrangeSaveOpen: false, arrangeSaveError: null, treeCollapsed: false
      });
    };

    // Leave Arrange, clearing its working state. Does NOT touch the cameras array —
    // callers set that first (save reselects; discard restores the pre-entry view).
    exitEditMode = () => {
      this.setState({
        editMode: false, arrangeConfirmCancel: false, arrangeDeleteConfirm: null,
        arrangeSaveOpen: false, arrangeSaveError: null, arrangeRedo: null, treeCollapsed: false
      });
      // Option A (#86bb7phhh): if Arrange was entered from Playback's Views sheet, return there once
      // it's done (save) or cancelled. Normal in-Live arrange leaves _arrangeReturnTo null (no nav).
      if (this._arrangeReturnTo) {
        const dest = this._arrangeReturnTo;
        this._arrangeReturnTo = null;
        history.push(dest);
      }
    };

    // Cross-screen Arrange entry (Option A, #86bb7phhh): Playback's shared ViewList routes
    // "Arrange current view" / "New view" / per-view ✎ here via
    // history.push('/live', { arrangeRequest: 'current' | 'new' | { view }, returnTo }). Enter the
    // matching Arrange mode on the (shared) view and remember where to return on exit.
    consumeArrangeRequest = () => {
      const st = (history.location && history.location.state) || null;
      if (!st || !st.arrangeRequest) return;
      this._arrangeReturnTo = st.returnTo || null;
      // Clear the router state so a refresh / browser-back can't re-enter Arrange.
      try { history.replace(history.location.pathname, {}); } catch (e) { /* no-op */ }
      const req = st.arrangeRequest;
      if (req === 'new') this.enterNewView();
      else if (req === 'current') this.enterEditMode();
      else if (req && req.view) this.arrangeSpecificView(req.view);
    };

    // Restore the live view as it was before Arrange, then leave (cancel / discard).
    exitAndRestore = () => {
      if (this.state.arrangePreEntry) this.props.setCameraArrangement(this.state.arrangePreEntry.map(c => ({ ...c })), false);
      this.exitEditMode();
    };

    // Dirty vs the entry baseline (a revert reads clean again).
    arrangeIsDirty = () => !arrangementsEqual(this.props.cameras || [], this.state.arrangeOriginal);

    // Save enables only when named + ≥1 camera placed + a real change (David).
    arrangeCanSave = () => {
      const hasName = !!(this.state.arrangeName && this.state.arrangeName.trim());
      const hasCam = (this.props.cameras || []).some(c => c && c.fEnable);
      return hasName && hasCam && this.arrangeIsDirty();
    };

    // Clamp a wide arrange density (32/All) back to 16 when the viewport is no longer in the
    // landscape+desktop tier (fired by the ARRANGE_WIDE_TIER_MQ matchMedia listener). The decision is
    // the pure, unit-tested clampWideDensity(); this applies it through the normal setter so the pages
    // re-window. No-op unless arranging AND on a wide density AND out of the wide tier.
    _clampArrangeDensityToTier = () => {
      if (!this.state.editMode) return;
      const isWide = !!(this._wideTierMql && this._wideTierMql.matches);
      const next = clampWideDensity(this.state.arrangeDensity, isWide);
      if (next != null) this.arrangeSetDensity(next);
    };

    arrangeSetDensity = (density) => {
      const { pageCount, page } = rechunkForDensity(this.props.cameras || [], density, this.state.arrangePage);
      this.setState({ arrangeDensity: density, arrangePageCount: pageCount, arrangePage: page });
    };

    arrangeGoToPage = (page) => this.setState({ arrangePage: page });

    arrangeAddPage = () => {
      if (!canAddArrangePage(this.state.arrangePageCount, this.state.arrangeDensity)) return;
      const arrangePageCount = this.state.arrangePageCount + 1;
      this.setState({ arrangePageCount, arrangePage: arrangePageCount - 1 });
    };

    // Delete a page: an EMPTY page goes immediately; a page WITH cameras confirms first
    // (David). Later pages shift up + renumber (deletePageSlots).
    arrangeDeletePage = (page) => {
      const count = pageFillCount(this.props.cameras || [], this.state.arrangeDensity, page);
      if (count > 0) { this.setState({ arrangeDeleteConfirm: { page, count } }); return; }
      this.doDeletePage(page);
    };

    doDeletePage = (page) => {
      const density = this.state.arrangeDensity;
      const serial = this.arrangeSerial();
      const next = deletePageSlots(this.props.cameras || [], page, density, (i) => blankSlot(i, serial));
      this.props.setCameraArrangement(next, true);
      const arrangePageCount = Math.max(1, this.state.arrangePageCount - 1);
      const arrangePage = Math.min(this.state.arrangePage, arrangePageCount - 1);
      this.setState({ arrangePageCount, arrangePage, arrangeDeleteConfirm: null });
    };

    // Reset → restore the entry baseline (new = blank · edit = the unedited view),
    // snapshotting the pre-reset state for a single-level "Undo reset".
    arrangeReset = () => {
      this.setState({ arrangeRedo: this.snapshotCameras() });
      this.props.setCameraArrangement((this.state.arrangeOriginal || []).map(c => ({ ...c })), false);
    };

    arrangeUndoReset = () => {
      if (!this.state.arrangeRedo) return;
      this.props.setCameraArrangement(this.state.arrangeRedo.map(c => ({ ...c })), true);
      this.setState({ arrangeRedo: null });
    };

    arrangeCancel = () => {
      if (this.arrangeIsDirty()) { this.setState({ arrangeConfirmCancel: true }); return; }
      this.exitAndRestore();
    };

    arrangeDiscard = () => { this.setState({ arrangeConfirmCancel: false }); this.exitAndRestore(); };

    toggleTree = () => this.setState(p => ({ treeCollapsed: !p.treeCollapsed }));

    // Unified Save (reuses saveView + ViewModal): scope + Update-vs-new. On success,
    // leave Arrange and reselect the saved view (which rebuilds the live grid).
    arrangeOpenSave = () => { if (this.arrangeCanSave()) this.setState({ arrangeSaveOpen: true }); };
    arrangeCloseSave = () => this.setState({ arrangeSaveOpen: false });

    arrangeSaveConfirm = (viewData) => {
      const { sSess, bUserID } = this.props;
      const { sName, fGlobal, fDefault, fDividia, overwrite } = viewData;
      this.props.saveView(sSess, bUserID, sName, fGlobal, fDefault, !!overwrite, null, fDividia)
        .then((savedName) => {
          this.props.clearViewDirty();
          this.exitEditMode();
          this.props.selectView(savedName);
        })
        .catch((err) => {
          this.setState({ arrangeSaveOpen: false, arrangeSaveError: (err && err.message) || 'Failed to save view' });
        });
    };

    // The view object the Save modal targets: on EDIT with the name unchanged, offer to
    // Update the loaded view; on New — or an edit whose name was changed in the top bar —
    // save as a NEW view (never silently overwrite the differently-named original).
    arrangeSaveTargetView = () => {
      if (this.state.arrangeMode !== 'edit') return null;
      const name = (this.state.arrangeName || '').trim().toLowerCase();
      const current = (this.props.currentView || '').toLowerCase();
      if (!name || name !== current) return null;
      return (this.props.views || []).find(v => !v.isSeparator && v.sName === this.props.currentView) || null;
    };

    // Get page dot info for mobile camera group navigation
    // Note: quadViewEnabledArr etc. already contain one entry per page/group
    // (e.g. ["1-4", "5-8", "9-12", "13-16"]), so .length IS the page count.
    getPageDots = () => {
      const { conf, twoViewEnabledArr, quadViewEnabledArr, sixViewEnabledArr, singleViewEnabledArr,
              eightViewEnabledArr, nineViewEnabledArr, twelveViewEnabledArr, sixteenViewEnabledArr } = this.props;

      switch (conf) {
        case 'conf-1': {
          const totalPages = Math.ceil((singleViewEnabledArr ? singleViewEnabledArr.length : 0) / 16);
          const currentPage = this.props.currentCamGroupSingle || 1;
          return { totalPages, currentPage, camsPerPage: 16 };
        }
        case 'conf-2': {
          const totalPages = twoViewEnabledArr ? twoViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-2';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 2);
          return { totalPages, currentPage, camsPerPage: 2 };
        }
        case 'conf-4': {
          const totalPages = quadViewEnabledArr ? quadViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-4';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 4);
          return { totalPages, currentPage, camsPerPage: 4 };
        }
        case 'conf-6': {
          const totalPages = sixViewEnabledArr ? sixViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-6';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 6);
          return { totalPages, currentPage, camsPerPage: 6 };
        }
        case 'conf-8': {
          const totalPages = eightViewEnabledArr ? eightViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-8';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 8);
          return { totalPages, currentPage, camsPerPage: 8 };
        }
        case 'conf-9': {
          const totalPages = nineViewEnabledArr ? nineViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-9';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 9);
          return { totalPages, currentPage, camsPerPage: 9 };
        }
        case 'conf-12': {
          const totalPages = twelveViewEnabledArr ? twelveViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-12';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 12);
          return { totalPages, currentPage, camsPerPage: 12 };
        }
        case 'conf-16': {
          const totalPages = sixteenViewEnabledArr ? sixteenViewEnabledArr.length : 1;
          const camStr = this.props.currentCamView || 'cam_1-16';
          const firstCam = parseInt(camStr.split('_')[1]) || 1;
          const currentPage = Math.ceil(firstCam / 16);
          return { totalPages, currentPage, camsPerPage: 16 };
        }
        default:
          return { totalPages: 1, currentPage: 1, camsPerPage: 4 };
      }
    }

    // Navigate to a specific camera page (dot tap or swipe)
    goToPage = (pageIndex) => {
      const { conf } = this.props;
      this.killAutoScan();

      const camsPerPage = { 'conf-1': 16, 'conf-2': 2, 'conf-4': 4, 'conf-6': 6, 'conf-8': 8, 'conf-9': 9, 'conf-12': 12, 'conf-16': 16 };
      const perPage = camsPerPage[conf] || 4;
      const startCam = (pageIndex - 1) * perPage + 1;

      if (conf === 'conf-1') {
        this.props.setCamGroupSingle(pageIndex);
        this.props.camViewChange(`cam_${startCam}`);
      } else {
        const endCam = startCam + perPage - 1;
        this.props.camViewChange(`cam_${startCam}-${endCam}`);
        if (conf === 'conf-4') this.props.setCamGroupQuad(pageIndex <= 8 ? 1 : 2);
      }
    }

    // Swipe handlers for mobile camera paging
    handleTouchStart = (e) => {
      this._touchStartX = e.touches[0].clientX;
    }

    handleTouchEnd = (e) => {
      if (!this._touchStartX) return;
      const deltaX = e.changedTouches[0].clientX - this._touchStartX;
      this._touchStartX = null;

      if (Math.abs(deltaX) < 50) return; // Minimum swipe distance

      const { totalPages, currentPage } = this.getPageDots();
      if (deltaX < 0 && currentPage < totalPages) {
        // Swipe left = next page
        this.goToPage(currentPage + 1);
      } else if (deltaX > 0 && currentPage > 1) {
        // Swipe right = previous page
        this.goToPage(currentPage - 1);
      }
    }

    render() {
        const containerClass = `live-container ${this.props.useEviewLogin ? 'eview-mode' : ''} ${this.state.editMode ? 'live-container--arrange' : ''} ${this.state.mouseIdle && !this.state.editMode ? 'live-container--idle' : ''}`;
        const leftClass = `live-left ${this.props.fFullscreen ? 'live-left--fullscreen' : ''}`;
        const videoWrapperClass = `live-video-wrapper ${this.props.fFullscreen ? 'live-video-wrapper--fullscreen' : ''}`;
        // NVR clock split into date + time so the Live header carries both (#86bb5dx62, David).
        const nvrClock = formatNvrClock(this.props.nvrTimestamp);
        // Days of recorded footage for the mobile chrome (#86bb5dx62) — same gate the desktop bottom
        // bar uses: only when status loaded AND the system has enabled cameras (hide "0 Days" on a
        // cam-less eView controller). null → the ⓘ chip shows the icon alone.
        const _ms = this.props.machineStatus;
        const _hasEnabledCams = Array.isArray(this.props.cameras) && this.props.cameras.some(
            (c) => c && c.fEnable === true && (c.bSerial === undefined || c.bSerial === this.props.bSerial));
        const daysRec = (_ms && typeof _ms.days === 'number' && _hasEnabledCams) ? _ms.days : null;
        // Product mode drives the accent (blue dview/cloud, green eview) and the header badge.
        // CLOUD (#86bb5dx62): LIVE is display-uniform across cloud device types (dragon vs legacy
        // differ only in Playback), so one cloud mode drives the CLOUD badge + blue accent
        // (`data-mode="cloud-modern"` already has the blue accent rule in liveStyle.css). The
        // modern/legacy split is a per-tile Playback concern, not a chrome-level one.
        const mode = this.props.productMode === PRODUCT_MODE.CLOUD
            ? 'cloud-modern'
            : (this.props.fEview ? 'eview' : 'dview');
        // Jump is LOCAL/eView-only (a login into another NVR). Cloud's one token covers all systems,
        // so cloud has NO jump — hide every jump entry point (action-bar + landscape rail buttons),
        // leaving Views as the sole navigation (David #86bb5dx62). Named capability, not the mode.
        const canJump = getProductConfig().hasJump;

        // Arrange-mode computed flags (only meaningful in editMode).
        const arrangeDirty = this.state.editMode && this.arrangeIsDirty();
        const canSaveArrange = this.state.editMode && this.arrangeCanSave();
        const arrangeCanAdd = canAddArrangePage(this.state.arrangePageCount, this.state.arrangeDensity);
        const arrangeSaveTarget = this.state.arrangeSaveOpen ? this.arrangeSaveTargetView() : null;

        return (
            <div className={containerClass} id="live_view" data-mode={mode}>

                {/* Top Arrange bar (#86bb5dx62): the ONLY chrome in Arrange mode — Cancel ·
                    eyebrow + editable name · Reset/Undo · Density · Save. Replaces the Live
                    header/action-bar/nav (hidden via .live-container--arrange). Tablet+desktop. */}
                { this.state.editMode &&
                  <div className="live-arrange-bar" data-mode={mode}>
                    <button className="live-arrange-bar__cancel" onClick={this.arrangeCancel}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                      <span>Cancel</span>
                    </button>
                    <div className="live-arrange-bar__title">
                      <span className="live-arrange-bar__eyebrow">{this.state.arrangeMode === 'new' ? 'New view' : 'Editing'}</span>
                      <label className="live-arrange-bar__namewrap" title="Edit view name">
                        <input
                          className="live-arrange-bar__name"
                          value={this.state.arrangeName}
                          placeholder="Name this view"
                          aria-label="View name"
                          onChange={(e) => this.setState({ arrangeName: e.target.value })}
                        />
                        <svg className="live-arrange-bar__name-ic" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                      </label>
                    </div>
                    <span className="live-arrange-bar__spacer" />
                    { (this.state.arrangeRedo && !arrangeDirty)
                      ? <button className="live-arrange-bar__ghost" onClick={this.arrangeUndoReset}>Undo reset</button>
                      : (arrangeDirty && <button className="live-arrange-bar__ghost" onClick={this.arrangeReset}>Reset</button>) }
                    <div className="live-arrange-bar__density">
                      <span className="live-arrange-bar__density-label">Density</span>
                      <div className="live-arrange-seg" role="group" aria-label="Tiles per page">
                        {ARRANGE_TILE_OPTIONS.map(n => (
                          <button
                            key={n}
                            className={'live-arrange-seg__btn'
                              + (this.state.arrangeDensity === n ? ' live-arrange-seg__btn--active' : '')
                              + (ARRANGE_WIDE_TILE_OPTIONS.includes(n) ? ' live-arrange-seg__btn--wide' : '')}
                            aria-pressed={this.state.arrangeDensity === n}
                            onClick={() => this.arrangeSetDensity(n)}
                          >{n === 64 ? 'All' : n}</button>
                        ))}
                      </div>
                    </div>
                    <button className="live-arrange-bar__save" disabled={!canSaveArrange} onClick={this.arrangeOpenSave}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg>
                      <span>Save</span>
                    </button>
                  </div>
                }

                {/* Mobile/tablet top bar — hidden on desktop via CSS */}
                { !this.props.fFullscreen &&
                  <MobileTopBar
                    sName={this.props.sName}
                    bSerial={this.props.bSerial}
                    fEview={this.props.fEview}
                    mode={mode}
                    nvrTime={nvrClock.time}
                    nvrDate={nvrClock.date}
                    userName={this.props.sUserName}
                    daysRec={daysRec}
                    usersOnline={this.props.usersOnline}
                    onInfoClick={this.openSysInfo}
                    onJump={() => this.toggleMobileMenu('jump')}
                    onViews={() => this.toggleMobileMenu('views')}
                    canJump={getProductConfig().hasJump}
                    viewDirty={this.liveViewDirty()}
                    viewLabel={this.props.currentView && !String(this.props.currentView).startsWith('__synthetic_') ? this.props.currentView : 'Views'}
                  />
                }

                {/* Desktop header (redesign #86bb5dx62, Mock D4) — logo · breadcrumb system/view ·
                    centered clock · Live/Playback · Setup · account. The wrapper is CSS-gated to
                    desktop (min-width:1024), so this never shows on mobile/tablet (they use
                    MobileTopBar). Replaces the old PageHeader here. */}
                { !this.props.fFullscreen &&
                  <div className="live-desktop-header">
                    <DesktopHeader
                      activePanel={this.state.desktopPanel}
                      onOpenPanel={this.toggleDesktopPanel}
                      onSetup={this.openSetup}
                      onPlayback={this.goToPlayback}
                      playbackDisabled={getViewDeviceType(this.props.currentView, this.props.viewSlots, this.props.allCameras ? Object.values(this.props.allCameras) : []) === 'legacy'}
                      viewLabel={this.viewLabel()}
                      viewDirty={this.liveViewDirty()}
                    />
                  </div>
                }

                {/* Main content row: rail + slide-out panel + video (desktop) / video + mobile chrome (mobile) */}
                <div className="live-main-row">
                  {/* Desktop slide-out panel — Jump / Views, opened from the header breadcrumb.
                      The standing left rail was removed (#86bb5dx62, David): Layout moved to the
                      bottom bar with the other viewing controls, and Jump/Views live on the always-
                      visible breadcrumb (click system → Jump, view → Views). The panel is a flex
                      sibling of .live-left, so opening it PUSHES the video narrower. Cameras are NOT
                      a standing tab — the camera tree appears only inside Views → Arrange.
                      Desktop-only via CSS. */}
                  { !this.props.fFullscreen &&
                    <div className={'live-dpanel' + (this.state.desktopPanel ? ' live-dpanel--open live-dpanel--' + this.state.desktopPanel : '')}>
                      {this.renderDesktopPanel()}
                    </div>
                  }
                  <div className={leftClass}>
                      <div className={videoWrapperClass}>
                          <div className="live-header-container">
                              <div className="live-video-stream"
                                   onTouchStart={this.handleTouchStart}
                                   onTouchEnd={this.handleTouchEnd}>
                                  {/* VideoViewWs handles both WebSocket and JPEG fallback */}
                                  <VideoViewWs
                                      onWebSocketFailure={this.handleWebSocketFailure}
                                      onWebSocketSuccess={this.handleWebSocketSuccess}
                                      wsBackendSupported={this.state.useWebSockets}
                                      onContextMenuOpen={this.killAutoScan}
                                      editMode={this.state.editMode}
                                      arrange={{
                                          density: this.state.arrangeDensity,
                                          page: this.state.arrangePage,
                                          pageCount: this.state.arrangePageCount,
                                          canAddPage: arrangeCanAdd,
                                          onSelectPage: this.arrangeGoToPage,
                                          onAddPage: this.arrangeAddPage,
                                          onDeletePage: this.arrangeDeletePage,
                                          treeCollapsed: this.state.treeCollapsed,
                                          onToggleTree: this.toggleTree,
                                      }}
                                  />
                              </div>
                          </div>
                      </div>
                      { !this.props.fFullscreen ?
                        <div className="live-cam-buttons-wrapper">
                          <CamButtonsView autoScanning={ this.state.autoScanning } toggleAutoScan={ this.toggleAutoScan } />
                          {/* The landscape Layout/conf picker moved OFF this strip into the right rail
                              (under Overlays, David #86bb7phhh). Portrait keeps Layout in the action bar. */}
                        </div> :
                        null
                      }
                  </div>

                {/* Mobile bottom area - hidden on desktop via CSS */}
                { !this.props.fFullscreen &&
                  <>
                    {/* Page dots removed (redesign #86bb5dx62) — the camera-bank
                        strip under the video now indicates the current set. */}

                    {/* Action bar: Views / Layout / Jump / Scan (redesign #86bb5dx62).
                        Same handlers as before — restyled to the mock, overlays now
                        slide up as bottom sheets with a scrim. */}
                    <div className="live-action-bar live-mobile-controls">
                      {/* Layout sheet (was the grid picker) — shared <Sheet> (grip + corner ✕). Each
                          action-bar sheet brings its own scrim now, so there's no separate shared scrim. */}
                      { this.state.mobileMenuOpen === 'grid' &&
                        <Sheet className="live-sheet live-sheet--layout" scrimClassName="live-sheet-scrim" title="Layout" ariaLabel="Layout" onClose={this.closeMobileMenu}>
                          {this.renderLayoutGrid()}
                        </Sheet>
                      }

                      {/* Resting action bar (hidden in Arrange, where the top bar takes over). */}
                      { !this.state.editMode &&
                        <>
                      <button className={`live-action-btn live-mobile-controls__btn ${this.state.mobileMenuOpen === 'grid' ? 'live-action-btn--active' : ''}`} onClick={() => this.toggleMobileMenu('grid')}>
                        {/* Accurate active-conf glyph (shared confGlyph) — was a static 4-up icon that made
                            every layout read as conf-4. Portrait shows the transposed 2×3 / 3×4 shapes. The
                            label is the layout's tile count (e.g. "6"), matching the picker. */}
                        {confGlyph(this.props.conf || 'conf-4', isMobilePortrait())}
                        <span className="live-action-btn__label">{(this.props.conf || 'conf-4').replace('conf-','')}</span>
                      </button>
                      <button className={`live-action-btn live-action-btn--views live-mobile-controls__btn ${this.state.mobileMenuOpen === 'views' ? 'live-action-btn--active' : ''}`} onClick={() => this.toggleMobileMenu('views')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z"/>
                        </svg>
                        <span className="live-action-btn__label">Views</span>
                        {this.liveViewDirty() && <span className="live-action-btn__dot" title="Unsaved changes" />}
                      </button>
                      { canJump &&
                      <button className={`live-action-btn live-action-btn--jump live-mobile-controls__btn ${this.state.mobileMenuOpen === 'jump' ? 'live-action-btn--active' : ''}`} onClick={() => this.toggleMobileMenu('jump')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"/>
                        </svg>
                        <span className="live-action-btn__label">Jump</span>
                      </button> }
                      <button className="live-action-btn live-action-btn--scan live-mobile-controls__btn" data-on={this.state.autoScanning ? 'true' : 'false'} onClick={this.toggleAutoScan}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                        </svg>
                        <span className="live-action-btn__label">Scan</span>
                      </button>
                      {/* Overlays (#86bb5dx62) — opens the Camera-overlays sheet. In the action bar now
                          (off the video — nothing floats over the tiles). "tune" icon, not a gear. */}
                      <button className={`live-action-btn live-mobile-controls__btn ${this.state.mobileMenuOpen === 'overlays' ? 'live-action-btn--active' : ''}`} onClick={() => this.toggleMobileMenu('overlays')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/>
                        </svg>
                        <span className="live-action-btn__label">Overlays</span>
                      </button>
                      {/* Fullscreen (#86bb7phhh) — reuses the desktop conf-fs mode (hides chrome to maximize
                          the grid; the floating exit button appears in fullscreen). CSS-gated to tablet+ for
                          now — a mobile fullscreen affordance is a later follow-up (David 2026-08-07). */}
                      <button className="live-action-btn live-action-btn--fs live-mobile-controls__btn" onClick={() => this.props.configChange('conf-fs')} title="Fullscreen">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                        </svg>
                        <span className="live-action-btn__label">Fullscreen</span>
                      </button>
                      {/* Arrange lives in the Views sheet now (with New view) — view management
                          in one place (David). No separate action-bar button. */}
                        </>
                      }

                      {/* Views sheet */}
                      { this.state.mobileMenuOpen === 'views' &&
                        <Sheet className="live-sheet live-mobile-overlay-panel" scrimClassName="live-sheet-scrim" grip={false} title="Views" ariaLabel="Views" onClose={this.closeMobileMenu}>
                          <div className="live-mobile-overlay-panel__content">
                            {/* Arrange current view + New view now live INSIDE ViewList so Live and
                                Playback share ONE views list (David #86bb7phhh). Still tablet+ only
                                (.live-arrange-entries CSS-hidden on mobile). Tap a row to watch it, ✎ to
                                Arrange that view, ⋯ to Rename/Delete; contextual Save on unsaved edits. */}
                            <ViewList
                              onArrange={this.arrangeSpecificView}
                              onArrangeCurrent={() => { this.closeMobileMenu(); this.enterEditMode(); }}
                              onNewView={() => { this.closeMobileMenu(); this.enterNewView(); }}
                              onClose={this.closeMobileMenu}
                            />
                          </div>
                        </Sheet>
                      }

                      {/* Jump sheet */}
                      { this.state.mobileMenuOpen === 'jump' &&
                        <Sheet className="live-sheet live-mobile-overlay-panel" scrimClassName="live-sheet-scrim" grip={false} title="Jump System" ariaLabel="Jump System" onClose={this.closeMobileMenu}>
                          <div className="live-mobile-overlay-panel__content" ref={el => {
                            if (el) { el.querySelectorAll('select').forEach(s => {
                              // Cap the listbox height so a 100+ server eView list is a compact
                              // scrollable box (and the type-ahead search filters within it), not a
                              // giant list that leaves empty rows once filtered (#86bb5dx62).
                              if (!s.getAttribute('size')) s.setAttribute('size', Math.min(Math.max(s.options.length, 4), 10));
                            }); }
                          }}>
                            <JumpControl onJumpStarted={() => this.closeMobileMenu()} />
                          </div>
                        </Sheet>
                      }
                    </div>

                    {/* Bottom nav: Live / Playback / Setup (redesign #86bb5dx62).
                        Setup opens the display-settings overlay; logout lives inside it. */}
                    <MobileBottomNav
                      activePage="live"
                      fPlayback={this.props.fPlayback}
                      mobileMenuOpen={this.state.mobileMenuOpen}
                      onToggleMenu={(m) => this.toggleMobileMenu(m)}
                      onSetup={this.openSetup}
                      guardNav={this.guardedNav}
                      onPlayback={this.goToPlayback}
                    />

                    {/* Unsaved-view guard (#86bb5dx62): prompt before leaving Live with a dirty view */}
                    { this.state.navGuard.open &&
                      <Sheet
                        className="live-sheet live-tile-sheet"
                        scrimClassName="live-sheet-scrim"
                        title="Unsaved view changes"
                        ariaLabel="Unsaved view changes"
                        onClose={this.navGuardCancel}
                      >
                          <p style={{ margin: '-4px 0 10px', fontSize: 'var(--live-font-xs)', color: 'var(--theme-text-muted)' }}>
                            You've rearranged the cameras. Save this view, or discard the changes before leaving?
                          </p>
                          <button className="live-tile-action" onClick={this.navGuardSave}>
                            <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/></svg></span>
                            <span>Save view…</span>
                          </button>
                          <button className="live-tile-action live-tile-action--danger" onClick={this.navGuardDiscard}>
                            <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg></span>
                            <span>Discard &amp; leave</span>
                          </button>
                          <button className="live-tile-action" onClick={this.navGuardCancel}>
                            <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></span>
                            <span>Keep editing</span>
                          </button>
                      </Sheet>
                    }

                    {/* Camera-overlays bottom sheet (#86bb5dx62) — the mobile/tablet home for the
                        desktop overlays gear, opened by the floating gear above. Rendered as a sibling
                        (not inside the action bar) so it shows on every non-desktop breakpoint, incl.
                        mobile-landscape where the action bar is replaced by the rail. */}
                    <CameraOverlaysSheet
                      visible={this.state.mobileMenuOpen === 'overlays'}
                      onClose={this.closeMobileMenu}
                      onSnapshotAll={this.onSnapshotAllTiles}
                      onResetZoom={this.onResetAllZoom}
                    />

                    {/* System-details sheet (#86bb5dx62) — mobile/tablet home for the desktop ⓘ,
                        opened by the top-bar ⓘ (portrait) or the landscape rail Info button. */}
                    <SystemInfoSheet visible={this.state.sysInfoOpen} onClose={this.closeSysInfo} />

                    {/* Landscape sidebar (#86bb5dx62): identity (system / view / time) at the top, then
                        Layout / Views / Jump / Scan / Overlays, then Playback / ⓘ / Setup at the bottom.
                        Mobile-landscape only (landscape @media; display:none in portrait). The identity
                        replaces the old floating pill — nothing floats over the video (David). The wide
                        landscape viewport affords the sidebar's width; Logout lives inside Setup, and
                        the bank pill strip under the video handles paging. */}
                    <div className="live-landscape-bar">
                      { !this.props.fFullscreen &&
                        <RailClock date={nvrClock.date} time={nvrClock.time} daysRec={daysRec} onInfo={this.openSysInfo} />
                      }
                      {/* Identity selectors (David #86bb7phhh): each button DISPLAYS its current value AND
                          opens its menu — Jump = the current server/NVR, Views = the current view. Mirrors
                          playback's Views-as-identity so the two landscape rails read as one. */}
                      <div className="live-landscape-bar__group">
                        { canJump &&
                        <button className={`live-landscape-bar__btn live-landscape-bar__btn--ident ${this.state.mobileMenuOpen === 'jump' ? 'live-landscape-bar__btn--active' : ''}`} title={`Jump — ${this.props.sName}`} onClick={() => this.toggleMobileMenu('jump')}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z"/></svg>
                          <span className="live-landscape-bar__label">{this.props.sName}</span>
                        </button> }
                        <button className={`live-landscape-bar__btn live-landscape-bar__btn--ident ${this.state.mobileMenuOpen === 'views' ? 'live-landscape-bar__btn--active' : ''}`} title={`Views — ${this.viewLabel()}`} onClick={() => this.toggleMobileMenu('views')}>
                          {/* Line-weight list icon (David #86bb7phhh) — the "list rows" shape of playback's
                              Views icon, but a thin line style that matches Live's other rail icons (the
                              filled FA icon read too heavy among them). */}
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h2v2H4V6zm4 0h12v2H8V6zM4 11h2v2H4v-2zm4 0h12v2H8v-2zM4 16h2v2H4v-2zm4 0h12v2H8v-2z"/></svg>
                          {this.liveViewDirty() && <span className="live-action-btn__dot live-action-btn__dot--rail" title="Unsaved changes" />}
                          <span className="live-landscape-bar__label">{this.viewLabel()}</span>
                        </button>
                      </div>
                      {/* Grid tools — Scan · Overlays. */}
                      <div className="live-landscape-bar__group live-landscape-bar__group--sep">
                        <button className="live-landscape-bar__btn live-landscape-bar__btn--scan" data-on={this.state.autoScanning ? 'true' : 'false'} title="Auto Scan" onClick={this.toggleAutoScan}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
                          <span className="live-landscape-bar__label">Scan</span>
                        </button>
                        {/* Overlays (#86bb5dx62) — "tune" icon (consistent with desktop + portrait). */}
                        <button className={`live-landscape-bar__btn ${this.state.mobileMenuOpen === 'overlays' ? 'live-landscape-bar__btn--active' : ''}`} title="Camera overlays" onClick={() => this.toggleMobileMenu('overlays')}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z"/></svg>
                          <span className="live-landscape-bar__label">Overlays</span>
                        </button>
                      </div>
                      {/* Layout/conf selector — moved off the camera-set strip into the rail, under Overlays
                          with its own separator (David #86bb7phhh). The glyph reflects the current conf and
                          opens the same Layout sheet. */}
                      <div className="live-landscape-bar__group live-landscape-bar__group--sep">
                        <button className={`live-landscape-bar__btn ${this.state.mobileMenuOpen === 'grid' ? 'live-landscape-bar__btn--active' : ''}`} title="Layout" onClick={() => this.toggleMobileMenu('grid')}>
                          {confGlyph(this.props.conf || 'conf-4', false)}
                          {/* Show the conf NUMBER, not the word "Layout" — matches how the conf selector
                              renders everywhere else (David #86bb7phhh). */}
                          <span className="live-landscape-bar__label">{(this.props.conf || 'conf-4').replace('conf-', '')}</span>
                        </button>
                        {/* Fullscreen — the mobile-landscape ENTER point (the action bar is a hidden sliver
                            here; its buttons re-home to this rail). Grouped with Layout as a grid/view tool.
                            David 2026-09-10. */}
                        <button className="live-landscape-bar__btn" title="Fullscreen" onClick={() => this.props.configChange('conf-fs')}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
                          <span className="live-landscape-bar__label">Fullscreen</span>
                        </button>
                      </div>
                      {/* Nav — Live (current) · Playback · Setup, matching the portrait bottom nav
                          (Setup is a destination, so it lives with Live/Playback — David #86bb5dx62).
                          Pinned to the bottom of the sidebar, separated from the controls above. */}
                      <div className="live-landscape-bar__group live-landscape-bar__group--nav">
                        {/* Nav shows only where you can GO — the current page (Live) has no "you are here"
                            button (David #86bb7phhh), matching the shared MobileBottomNav. Playback → the
                            multi-cam view (/mcplayback), shown per the fPlayback USER RIGHT. */}
                        {this.props.fPlayback === 1 &&
                          <button className="live-landscape-bar__btn" title="Playback" onClick={this.goToPlayback}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/></svg>
                            <span className="live-landscape-bar__label">Playback</span>
                          </button>
                        }
                        {/* Setup opens the display-settings overlay (logout lives inside it). */}
                        <button className="live-landscape-bar__btn" title="Setup" onClick={this.openSetup}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/></svg>
                          <span className="live-landscape-bar__label">Setup</span>
                        </button>
                      </div>
                    </div>

                  </>
                }

                </div>{/* close live-main-row */}

                {/* Desktop bottom bar (redesign #86bb5dx62, Mock D4) — full-width below the body row:
                    Scan · Fullscreen · Overlays gear (left) · camera banks centered on the video
                    (center) · status + (i) system details (right). Desktop-only via CSS; replaces the
                    old right sidebar (ConfButtons → Layout panel, Views dropdown → Views panel,
                    Jump → Jump panel, ControlBox → account menu, Status → this bar). */}
                { !this.props.fFullscreen &&
                  <DesktopBottomBar
                    autoScanning={this.state.autoScanning}
                    toggleAutoScan={this.toggleAutoScan}
                    conf={this.props.conf}
                    onSelectLayout={this.selectGridLayout}
                    onFullscreen={() => this.props.configChange('conf-fs')}
                    onSnapshotAll={this.onSnapshotAllTiles}
                    onResetZoom={this.onResetAllZoom}
                  />
                }
                { this.props.fFullscreen ?
                    <>
                      <ContextMenu
                        style={{ color: 'black'}}
                        contextId={'live_view'}
                        closeOnClick
                        items={[
                          {
                            label: 'Exit FullScreen',
                            onClick: this.exitFullscreenHamdler
                          }
                        ]} />
                      {/* Fullscreen bar (#86bb7phhh; functional-controls rework David 2026-09-10) — always
                          visible, every control tappable: View (→ Views picker) · conf (→ Layout picker) ·
                          Exit. PHONES: pinned to the TOP (clears iOS Safari's bottom URL/toolbar), page-sets
                          hidden; TABLET/DESKTOP: bottom bar with the page-sets strip (see the .live-fs-bar
                          media overrides in liveStyle.css). Replaces the floating exit that used to cover a
                          corner tile's ⋯. */}
                      <div className="live-fs-bar">
                        {/* View — functional: opens the Views picker in-fullscreen via a dedicated
                            fsViewsOpen sheet (the action bar + its mobileMenuOpen sheets are unmounted in
                            fullscreen — line 1331). Shows the current view name, or "Views" when none/
                            synthetic. David 2026-09-10. */}
                        <button className="live-fs-bar__btn live-fs-bar__viewbtn" onClick={this.toggleFsViews} title="Views" aria-label="Change view">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h2v2H4V6zm4 0h12v2H8V6zM4 11h2v2H4v-2zm4 0h12v2H8v-2zM4 16h2v2H4v-2zm4 0h12v2H8v-2z"/></svg>
                          <span className="live-fs-bar__view">{this.props.currentView && !String(this.props.currentView).startsWith('__synthetic_') ? this.props.currentView : 'Views'}</span>
                        </button>
                        {this.props.conf && this.props.conf !== 'conf-1' && (
                          <div className="live-fs-bar__sets">
                            <CamButtonsView hideAutoScan />
                          </div>
                        )}
                        <div className="live-fs-bar__actions">
                          <button className="live-fs-bar__btn live-fs-bar__grid" onClick={this.toggleFsGrid} title="Layout" aria-label="Change layout">
                            {confGlyph(this.props.conf || 'conf-4', false)}
                            <span className="live-fs-bar__conf-num">{(this.props.conf || 'conf-4').replace('conf-', '')}</span>
                          </button>
                          <button className="live-fs-bar__btn live-fs-bar__exit" onClick={this.exitFullscreenHamdler} title="Exit Fullscreen (Esc)" aria-label="Exit fullscreen">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                            </svg>
                          </button>
                        </div>
                        {/* Portal both fullscreen pickers out to .live-container: the fs-bar's backdrop-filter
                            (+ z-index) makes it the containing block for their position:fixed scrim + panel,
                            trapping both inside the ~48px bar (scrim can't dim the video, panel reads as
                            see-through). Same fix the Sheet's portalSelector was built for (#86bbmxrtt). */}
                        {this.state.fsGridOpen && (
                          <Sheet className="live-sheet live-sheet--layout live-sheet--fs" scrimClassName="live-sheet-scrim" portalSelector=".live-container" title="Layout" ariaLabel="Layout" onClose={() => this.setState({ fsGridOpen: false })}>
                            <LayoutGrid conf={this.props.conf} onSelect={(c) => { this.selectGridLayout(c); this.setState({ fsGridOpen: false }); }} />
                          </Sheet>
                        )}
                        {/* Views picker in-fullscreen (mirrors fsGridOpen). ViewList's arrange/new callbacks
                            are optional — onClose-only gives a clean tap-to-switch (switch stays in
                            fullscreen). David 2026-09-10. */}
                        {this.state.fsViewsOpen && (
                          <Sheet className="live-sheet live-mobile-overlay-panel live-sheet--fs" scrimClassName="live-sheet-scrim" portalSelector=".live-container" grip={false} title="Views" ariaLabel="Views" onClose={() => this.setState({ fsViewsOpen: false })}>
                            <div className="live-mobile-overlay-panel__content">
                              <ViewList onClose={() => this.setState({ fsViewsOpen: false })} />
                            </div>
                          </Sheet>
                        )}
                      </div>
                    </> :
                    null
                }
                {/* spacer to move the previous container to the left */}
                { !this.props.fFullscreen ?
                    <div className="live-spacer"></div> :
                    null
                }

                {/* App version badge - mobile only (desktop version is in sidebar) */}
                {process.env.REACT_APP_VERSION && !this.props.fFullscreen && (
                    <div className="live-version-badge">
                        {versionTag(process.env.REACT_APP_VERSION)}
                    </div>
                )}

                {/* Jump error modal */}
                <AlertModal
                    show={!!this.props.jumpError}
                    message={this.props.jumpError}
                    type="error"
                    eviewMode={this.props.fEview}
                    onClose={() => this.props.clearJumpError()}
                />

                {/* The session's access changed while viewing (AuthActions.refreshCloudAccess). */}
                <AlertModal
                    show={!!this.props.accessNotice}
                    message={this.props.accessNotice}
                    type="info"
                    eviewMode={this.props.fEview}
                    onClose={() => this.props.clearAccessNotice()}
                />

                <AlertModal
                    show={!!this.state.legacyPlaybackAlert}
                    message="Playback is not available for legacy devices. Switch to a Modern view, or contact Dividia to discuss a cloud device upgrade."
                    type="info"
                    onClose={() => this.setState({ legacyPlaybackAlert: false })}
                />

                {/* Arrange: Cancel-with-changes confirm (never silently drop work). */}
                { this.state.arrangeConfirmCancel &&
                  <Sheet
                    className="live-sheet live-tile-sheet"
                    scrimClassName="live-sheet-scrim"
                    title="Discard changes?"
                    ariaLabel="Discard changes"
                    onClose={() => this.setState({ arrangeConfirmCancel: false })}
                  >
                      <p style={{ margin: '-4px 0 10px', fontSize: 'var(--live-font-xs)', color: 'var(--theme-text-muted)' }}>
                        You've arranged cameras but haven't saved. Keep editing, or discard the changes?
                      </p>
                      <button className="live-tile-action" onClick={() => this.setState({ arrangeConfirmCancel: false })}>
                        <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg></span>
                        <span>Keep editing</span>
                      </button>
                      <button className="live-tile-action live-tile-action--danger" onClick={this.arrangeDiscard}>
                        <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg></span>
                        <span>Discard &amp; leave</span>
                      </button>
                  </Sheet>
                }

                {/* Arrange: delete a page that still has cameras — confirm first (David). */}
                { this.state.arrangeDeleteConfirm &&
                  <Sheet
                    className="live-sheet live-tile-sheet"
                    scrimClassName="live-sheet-scrim"
                    title={`Remove Page ${this.state.arrangeDeleteConfirm.page + 1}?`}
                    ariaLabel="Delete page"
                    onClose={() => this.setState({ arrangeDeleteConfirm: null })}
                  >
                      <p style={{ margin: '-4px 0 10px', fontSize: 'var(--live-font-xs)', color: 'var(--theme-text-muted)' }}>
                        Page {this.state.arrangeDeleteConfirm.page + 1} has {this.state.arrangeDeleteConfirm.count} camera{this.state.arrangeDeleteConfirm.count === 1 ? '' : 's'}. Removing it shifts the later pages up.
                      </p>
                      <button className="live-tile-action live-tile-action--danger" onClick={() => this.doDeletePage(this.state.arrangeDeleteConfirm.page)}>
                        <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg></span>
                        <span>Remove page</span>
                      </button>
                      <button className="live-tile-action" onClick={() => this.setState({ arrangeDeleteConfirm: null })}>
                        <span className="live-tile-action__ic"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg></span>
                        <span>Keep page</span>
                      </button>
                  </Sheet>
                }

                {/* Arrange: the unified Save modal (reuses ViewModal + saveView). */}
                <ViewModal
                    mode="save"
                    open={this.state.arrangeSaveOpen}
                    onClose={this.arrangeCloseSave}
                    onConfirm={this.arrangeSaveConfirm}
                    currentView={arrangeSaveTarget}
                    allViews={this.props.views || []}
                    viewRole={this.props.viewRole}
                    isAdmin={!!this.props.fAdmin}
                    currentUserId={this.props.bUserID}
                    crossServer={isCrossServerSlots(this.props.cameras || [])}
                    initialName={this.state.arrangeName}
                />

                {/* Arrange: save-failure alert (keeps the arrangement so the user can retry). */}
                <AlertModal
                    show={!!this.state.arrangeSaveError}
                    message={this.state.arrangeSaveError}
                    type="error"
                    eviewMode={this.props.fEview}
                    onClose={() => this.setState({ arrangeSaveError: null })}
                />
        </div>
        );
    };
};

const mapStateToProps = state => {
    const {
      bNumCams,
      bSerial,
      dvsName,
      dvsVersion,
      serverUrl,
      fEview,
      activeServerSerial,
      singleViewEnabledArr,
      twoViewEnabledArr,
      quadViewEnabledArr,
      sixViewEnabledArr,
      eightViewEnabledArr,
      twelveViewEnabledArr,
      nineViewEnabledArr,
      sixteenViewEnabledArr,
      dataLoaded,
      sName,
      cameras,
      machineStatus,
      usersOnline,
      allCameras,
     } = state.server;
    const { fFullscreen, singleCamSelected } = state.video;
    const { conf } = state.config;
    const { currentCamView, currentCamGroupSingle } = state.camera;
    const { userCameras, sName: sUserName, fPlayback, bID: bUserID, bRole: viewRole, fAdmin } = state.user;
    const { platform, nvrTimestamp } = state.utility;
    const { sSess, isLoggedIn, useEviewLogin } = state.auth;
    const { views, currentView, isDirty, viewSlots, pristineCameras } = state.view;
    const { jumpError, isJumping, accessNotice } = state.navigation;
    return {
        // Product mode (local/eview stay 'local'; cloud is a separate REST backend). Drives the
        // cloud chrome branch (CLOUD badge, blue accent, cloud ⓘ gating) — #86bb5dx62.
        productMode: getProductMode(state),
        bNumCams,
        bSerial,
        dvsName,
        dvsVersion,
        serverUrl,
        fEview,
        activeServerSerial,
        singleViewEnabledArr,
        twoViewEnabledArr,
        quadViewEnabledArr,
        sixViewEnabledArr,
        eightViewEnabledArr,
        nineViewEnabledArr,
        twelveViewEnabledArr,
        sixteenViewEnabledArr,
        dataLoaded,
        sName,
        cameras,
        machineStatus,
        usersOnline,
        allCameras,
        fFullscreen,
        singleCamSelected,
        conf,
        currentCamView,
        currentCamGroupSingle,
        userCameras,
        sUserName,
        fPlayback,
        bUserID,
        viewRole,
        fAdmin,
        platform,
        nvrTimestamp,
        sSess,
        isLoggedIn,
        useEviewLogin,
        views,
        currentView,
        viewSlots,
        pristineCameras,
        isDirty,
        isJumping,
        jumpError,
        accessNotice
    }
};

export default connect(mapStateToProps, {
  configChange,
  setSingleView,
  camViewChange,
  getNvrTimestamp,
  setLocalTimestamp,
  seedCloudTimezone,
  setCamGroupSingle,
  setCamGroupQuad,
  autoSelectDefaultView,
  clearView,
  clearViewDirty,
  clearJumpError,
  clearAccessNotice,
  saveView,
  selectView,
  setCameraArrangement,
  setMcpbConf,
  setMcpbSlots
})(Live);
